import { useQueries } from "@tanstack/react-query";

import { useApiClient } from "@/api/ApiContext";
import { ApiError, unwrap } from "@/api/client";
import {
  SIM_POLL_INTERVAL_MS,
  isSimRunTerminal,
  simQueryKeys,
  type PreviewSimRunStatus,
} from "@/api/previewSim";
import { useCapability } from "@/auth/SessionContext";

/**
 * The sim status of several runs at once, body-free. Same key and semantics as
 * `usePreviewSimRunQuery` (404 is data: `null` = not a sim run), so it dedupes
 * with the selected run's own observer. `undefined` = not answered yet.
 */
export function useLabSimStatuses(
  projectId: string,
  refs: readonly string[],
): ReadonlyMap<string, PreviewSimRunStatus | null | undefined> {
  const client = useApiClient();
  const inspect = useCapability("preview.inspect");
  return useQueries({
    queries: refs.map((previewRunRef) => ({
      queryKey: simQueryKeys.run(projectId, previewRunRef),
      async queryFn(): Promise<PreviewSimRunStatus | null> {
        try {
          return unwrap(
            await client.GET(
              "/v1/projects/{projectId}/preview-sim-runs/{previewRunRef}",
              { params: { path: { projectId, previewRunRef } } },
            ),
          );
        } catch (error) {
          if (error instanceof ApiError && error.status === 404) {
            return null;
          }
          throw error;
        }
      },
      enabled: inspect.allowed && projectId.length > 0,
      staleTime: 0,
      gcTime: 0,
      retry: false,
      refetchInterval: (query: {
        state: { status: string; data: PreviewSimRunStatus | null | undefined };
      }) => {
        const data = query.state.data;
        return query.state.status !== "success" ||
          data === null ||
          data === undefined ||
          isSimRunTerminal(data)
          ? false
          : SIM_POLL_INTERVAL_MS;
      },
    })),
    combine: (results) =>
      new Map(refs.map((ref, index) => [ref, results[index]?.data] as const)),
  });
}
